/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Theme = 'light' | 'dark' | 'auto'
export type ReadingFontSize = 'sm' | 'base' | 'lg' | 'xl'
export type ReadingFontFamily = 'serif' | 'sans' | 'mono'

const LS_THEME       = 'theme'
const LS_FONT_SIZE   = 'reading:fontSize'
const LS_FONT_FAMILY = 'reading:fontFamily'

function resolveIsDark(theme: Theme): boolean {
  if (theme === 'dark') return true
  if (theme === 'light') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function applyThemeToDom(theme: Theme) {
  document.documentElement.classList.toggle('dark', resolveIsDark(theme))
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface UIContextValue {
  authModalOpen: boolean
  openAuthModal: () => void
  closeAuthModal: () => void
  // Theme
  theme: Theme
  setTheme: (t: Theme) => void
  isDark: boolean
  // Reading font size ('sm' | 'base' | 'lg' | 'xl')
  readingFontSize: ReadingFontSize
  setReadingFontSize: (s: ReadingFontSize) => void
  // Reading font family
  readingFontFamily: ReadingFontFamily
  setReadingFontFamily: (f: ReadingFontFamily) => void
}

const UIContext = createContext<UIContextValue | null>(null)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const UIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authModalOpen, setAuthModalOpen] = useState(false)

  const [theme, setThemeState] = useState<Theme>(() =>
    (localStorage.getItem(LS_THEME) as Theme | null) ?? 'auto',
  )

  const [readingFontSize, setFontSizeState] = useState<ReadingFontSize>(() =>
    (localStorage.getItem(LS_FONT_SIZE) as ReadingFontSize | null) ?? 'base',
  )

  const [readingFontFamily, setFontFamilyState] = useState<ReadingFontFamily>(() =>
    (localStorage.getItem(LS_FONT_FAMILY) as ReadingFontFamily | null) ?? 'serif',
  )

  // Apply theme to DOM whenever it changes
  useEffect(() => { applyThemeToDom(theme) }, [theme])

  // Also react to OS preference changes when theme === 'auto'
  useEffect(() => {
    if (theme !== 'auto') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => applyThemeToDom('auto')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  const setTheme = useCallback((t: Theme) => {
    localStorage.setItem(LS_THEME, t)
    setThemeState(t)
  }, [])

  const setReadingFontSize = useCallback((s: ReadingFontSize) => {
    localStorage.setItem(LS_FONT_SIZE, s)
    setFontSizeState(s)
  }, [])

  const setReadingFontFamily = useCallback((f: ReadingFontFamily) => {
    localStorage.setItem(LS_FONT_FAMILY, f)
    setFontFamilyState(f)
  }, [])

  const isDark = useMemo(() => resolveIsDark(theme), [theme])

  const openAuthModal  = useCallback(() => setAuthModalOpen(true), [])
  const closeAuthModal = useCallback(() => setAuthModalOpen(false), [])

  return (
    <UIContext.Provider value={{
      authModalOpen, openAuthModal, closeAuthModal,
      theme, setTheme, isDark,
      readingFontSize, setReadingFontSize,
      readingFontFamily, setReadingFontFamily,
    }}>
      {children}
    </UIContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useUI = (): UIContextValue => {
  const ctx = useContext(UIContext)
  if (!ctx) throw new Error('useUI must be used inside UIProvider')
  return ctx
}
